


















































































































































import { Component, Prop, Watch } from 'vue-property-decorator';
import MixinPage from '@/plugins/podolib/services/helper/mixins/page';
import { TAcquisition, TPatient } from '@/plugins/podolib/services/entities/EntityPatients';
import { TEntity } from '@/plugins/podolib/services/entities/Entity';
import { AnalysisType } from '@/components/analysis/steps/type/AnalysisStepType';

// Components
import PatientAcquisitionExportation from '@/components/patients/acquisition/exportation/PatientAcquisitionExportation.vue';
import ModuleVionicPatientResultsRecommendations from '@/components/modules/vionic/patients/results/ModuleVionicPatientResultsRecommendations.vue';
import PatientsResultsRunningDetailed from '@/components/patients/results/PatientsResultsRunningDetailed.vue';
import PatientsResultsRunningGlobal from '@/components/patients/results/PatientsResultsRunningGlobal.vue';
import PatientResultsWalkingGenerals from '@/components/patients/results/PatientResultsWalkingGenerals.vue';
import PatientResultsWalkingAngular from '@/components/patients/results/PatientResultsWalkingAngular.vue';
import PatientResultsWalkingDetailed from '@/components/patients/results/PatientResultsWalkingDetailed.vue';
import PatientResultsWalkingDegraded from '@/components/patients/results/PatientResultsWalkingDegraded.vue';
import PatientResultsErroneous from '@/components/patients/results/PatientResultsErroneous.vue';
import PatientAcquisitionDelete from '@/components/patients/acquisition/PatientAcquisitionDelete.vue';

export type TStep = {
  id: string;
  text: string;
  component: string;
};

interface IPagePatientResults extends MixinPage {
  patient: TEntity<TPatient>;
  acquisition: TEntity<TAcquisition>;

  totalSteps: number;
  compareAcquisition: any;
  steps: TStep[];
  magicShortcut(e: KeyboardEvent): void;
}

@Component<IPagePatientResults>({
  components: {
    PatientAcquisitionExportation,
    ModuleVionicPatientResultsRecommendations,
    PatientsResultsRunningDetailed,
    PatientsResultsRunningGlobal,
    PatientResultsWalkingGenerals,
    PatientResultsWalkingAngular,
    PatientResultsWalkingDetailed,
    PatientResultsWalkingDegraded,
    PatientResultsErroneous,
    PatientAcquisitionDelete,
  },
  async mounted() {
    this.totalSteps = this.steps.length;
    this.compareAcquisition = null;

    document.addEventListener('keyup', this.magicShortcut);
  },
  beforeDestroy() {
    document.removeEventListener('keyup', this.magicShortcut);
  },
  beforeRouteEnter(to, from, next) {
    // Analytics
    next((vm) => {
      vm.$pdc.analytics?.actions.trackSuccess('Analysis Viewed', {
        patientId: to.params.id,
        analysisId: to.params.acquisitionId,
        type: vm.acquisition?.analysisType,
      });
    });
  },
  async beforeRouteUpdate(to, from, next) {
    await next();
    // Analytics
    this.$pdc.analytics?.actions.trackSuccess('Analysis Viewed', {
      patientId: to.params.id,
      analysisId: to.params.acquisitionId,
      type: this.acquisition?.analysisType,
    });
  },
})
export default class PagePatientResults extends MixinPage {
  @Prop({ required: true }) patient!: TEntity<TPatient>;
  @Prop({ required: false }) compareAcquisitionResponsiveMode!: any;

  detailedPage = false;
  comparisonPage = false;

  stepSelected = 'generals';
  currentAcquisition = 'walking';

  compareAcquisition: any = null;
  selectedStepIndex = 1;
  totalSteps = 0;
  magicShortcutDialog = false;

  get acquisition() {
    return this.patient!.acquisitions.find((x: any) => x._id === this.$route.params.acquisitionId)!;
  }

  get lastPatientPathology() {
    let allPathologies = [];

    const patientPathologies = this.acquisition.pathologies;
    const patientCustomPathologies = this.acquisition.customPathologies;

    if (patientPathologies) {
      for (const lastPathology of patientPathologies) {
        allPathologies.push({
          name: lastPathology.reference.name[this.language],
          sinceDate: lastPathology.sinceDate
            ? lastPathology.sinceDate
            : this.$moment(new Date(), this.$pdc.helper!.dateFormatRelatedToSelectedLanguage).format(),
        });
      }
    }
    if (patientCustomPathologies) {
      for (const lastCustomPathology of patientCustomPathologies) {
        allPathologies.push({
          name: lastCustomPathology.name,
          sinceDate: lastCustomPathology.sinceDate
            ? lastCustomPathology.sinceDate
            : this.$moment(new Date(), this.$pdc.helper!.dateFormatRelatedToSelectedLanguage).format(),
        });
      }
    }

    allPathologies = allPathologies.sort((a: any, b: any) => {
      const keyA = new Date(a.sinceDate);
      const keyB = new Date(b.sinceDate);
      // Compare the 2 dates
      if (keyA < keyB) return 1;
      if (keyA > keyB) return -1;
      return 0;
    });

    return allPathologies.length ? allPathologies![0].name : null;
  }

  // Current language for pathologies
  get language() {
    return this.$pdc.helper?.isAnExistingLanguage(this.$store.getters['pdc/doctor/lang'])!;
  }

  get isRunningAcquisition() {
    return this.acquisition.analysisType === AnalysisType.Running;
  }

  get isValidActivity() {
    return this.$pdc.helper?.isValidActivity(this.acquisition);
  }

  get steps(): TStep[] {
    const result = [];

    if (!this.isValidActivity) {
      result.push(
        ...[
          {
            id: 'generals',
            text: this.$tc('commons.standards.general', 2) as string,
            component: 'PatientResultsErroneous',
          },
        ],
      );
    } else if (this.acquisition && this.acquisition.analysisType === AnalysisType.Running) {
      result.push(
        {
          id: 'overview',
          text: this.$t('commons.standards.overview') as string,
          component: 'PatientsResultsRunningGlobal',
        },
        {
          id: 'analyse',
          text: this.$tc('commons.standards.analysis', 1) as string,
          component: 'PatientsResultsRunningDetailed',
        },
      );
    } else if (
      this.acquisition &&
      this.acquisition.analysisType === AnalysisType.Walking &&
      this.$pdc.helper?.isDegradedActivity(this.acquisition)
    ) {
      result.push(
        ...[
          {
            id: 'generals',
            text: this.$tc('commons.standards.general', 2) as string,
            component: 'PatientResultsWalkingDegraded',
          },
        ],
      );
    } else {
      result.push(
        ...[
          {
            id: 'generals',
            text: this.$tc('commons.standards.general', 2) as string,
            component: 'PatientResultsWalkingGenerals',
          },
          {
            id: 'angular',
            text: this.$t('commons.standards.angular') as string,
            component: 'PatientResultsWalkingAngular',
          },
        ],
      );
    }

    return result;
  }
  get lang() {
    const currentLang = this.$pdc.doctor!.current.config.lang;
    return currentLang !== 'fr' && currentLang !== 'en' ? 'en' : this.$pdc.doctor!.current.config.lang;
  }
  get manual() {
    const type = this.acquisition?.analysisType === AnalysisType.Running ? 'running' : 'walking';
    return {
      url: this.$pdc.helper!.fromCdn('docs', `manual-interpretation-${type}-${this.lang}.pdf`),
      fileName: `interpretation-manual-${type}`,
    };
  }

  @Watch('steps')
  onTotalStepsChange() {
    // Refresh steps when acquisition type change
    if (this.totalSteps !== this.steps.length) this.selectedStepIndex = 1;
    this.totalSteps = this.steps.length;
  }
  @Watch('compareAcquisitionResponsiveMode', { deep: true })
  onCompareAcquisitionResponsiveModeChange() {
    // Refresh steps when acquisition type change
    this.compareAcquisition = this.compareAcquisitionResponsiveMode;
  }
  @Watch('acquisition', { immediate: true, deep: true })
  onPatientAcquisitionChange(acquisition: any, lastAcquisition: any) {
    const isRunningAcquisition = acquisition?.analysisType === AnalysisType.Running;
    this.compareAcquisition = null;

    if (
      this.$pdc.helper?.isGoodActivity(acquisition) &&
      this.$pdc.helper?.isGoodActivity(lastAcquisition) &&
      acquisition?.analysisType === lastAcquisition?.analysisType
    )
      return;

    // Manage step selected
    if (this.acquisition.computed_data?.valid !== 2) {
      this.stepSelected = isRunningAcquisition && this.isValidActivity ? 'overview' : 'generals';
    } else {
      const newCurrentAcquisition = isRunningAcquisition ? 'running' : 'walking';

      // If activity type change
      if (this.currentAcquisition !== newCurrentAcquisition && this.isValidActivity)
        this.stepSelected = isRunningAcquisition ? 'overview' : 'generals';

      // If we are on the detailed page and switch on running acquisition
      if (isRunningAcquisition && this.detailedPage && this.isValidActivity) {
        this.detailedPage = false;
        this.stepSelected = 'overview';
      }

      this.currentAcquisition = newCurrentAcquisition;
    }
  }
  @Watch('detailedPage')
  ondetailedPage(open: boolean) {
    if (open) this.stepSelected = '';
  }

  // TODO : remove this hidden features
  magicShortcut(e: KeyboardEvent) {
    if (e.altKey && e.ctrlKey && e.key === 'y') {
      if (this.acquisition && !this.magicShortcutDialog) this.magicShortcutDialog = true;
    }
  }

  goToComparisonPage() {
    this.$router.push({
      name: 'patient-comparison',
      params: {
        acquisitionCompareId: this.acquisition._id,
      },
    });
  }
  openExportDialog() {
    // (this.$refs.export as any).open(this.patient, this.acquisition);
    this.$pdc.bus!.emit('export:open', { patient: this.patient, acquisition: this.acquisition });
  }
  openExportExcelDialog(acquisition: TEntity<TAcquisition>) {
    this.$pdc.bus!.emit('export:excel:open', { patient: this.patient, acquisition: acquisition });
  }
  openDeleteDialog(acquisitionId: TEntity<TAcquisition>['_id']) {
    (this.$refs.delete as any).open(this.patient, acquisitionId);
  }

  mounted() {
    this.currentAcquisition = this.acquisition?.analysisType === AnalysisType.Running ? 'running' : 'walking';
  }
}
